import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Observable } from "@babylonjs/core/Misc/observable";
import { TimingTools } from "@babylonjs/core/Misc/timingTools";
import { SharedToonTextures } from "./sharedToonTextures";
class TextureLoadingModel {
    uniqueId;
    leftLoadCount;
    isRequesting;
    errorTextureDatas;
    constructor(uniqueId) {
        this.uniqueId = uniqueId;
        this.leftLoadCount = 0;
        this.isRequesting = true;
        this.errorTextureDatas = [];
    }
}
class TextureLoadInfo {
    observable;
    hasLoadError;
    constructor() {
        this.observable = new Observable();
        this.hasLoadError = false;
    }
}
class MmdTextureData {
    cacheKey;
    _scene;
    _assetContainer;
    _onLoad;
    _onError;
    _arrayBuffer;
    _texture;
    constructor(cacheKey, scene, assetContainer, urlOrTextureName, useLazyLoadWithBuffer, onLoad, onError) {
        this.cacheKey = cacheKey;
        this._scene = scene;
        this._assetContainer = assetContainer;
        this._onLoad = onLoad;
        this._onError = onError;
        this._arrayBuffer = null;
        this._texture = null;
        if (!useLazyLoadWithBuffer) {
            scene._loadFile(urlOrTextureName, (data) => {
                const arrayBuffer = this._arrayBuffer = data;
                this._createTexture(scene, assetContainer, cacheKey, arrayBuffer, onLoad, (message, exception) => {
                    this._arrayBuffer = null;
                    onError?.(message, exception);
                });
            }, undefined, true, true, (request, exception) => {
                onError?.(request ? request.status + " " + request.statusText : "", exception);
            });
        }
    }
    loadFromArrayBuffer(arrayBuffer) {
        this._arrayBuffer = arrayBuffer;
        this._createTexture(this._scene, this._assetContainer, this.cacheKey, this._arrayBuffer, this._onLoad, (message, exception) => {
            this._arrayBuffer = null;
            this._onError?.(message, exception);
        });
    }
    _createTexture(scene, assetContainer, textureName, arrayBuffer, onLoad, onError) {
        scene._blockEntityCollection = !!assetContainer;
        const texture = this._texture = new Texture("data:" + textureName, scene, undefined, undefined, undefined, () => {
            if (this._texture === null) {
                if (onLoad !== null)
                    TimingTools.SetImmediate(onLoad);
            }
            else {
                onLoad?.();
            }
        }, onError, arrayBuffer, true);
        texture._parentContainer = assetContainer;
        scene._blockEntityCollection = false;
        assetContainer?.textures.push(texture);
        texture.name = textureName;
    }
    get arrayBuffer() {
        return this._arrayBuffer;
    }
    get texture() {
        return this._texture;
    }
}
/**
 * MMD async texture loader
 *
 * The MMD async texture loader caches redundant textures across multiple materials and simplifies asynchronous textural loading
 *
 * It also includes handling when multiple models are loaded at the same time
 */
export class MmdAsyncTextureLoader {
    /**
     * Observable which is notified when all textures of the model are loaded
     *
     * key: uniqueId, value: Observable<void>
     */
    onModelTextureLoadedObservable = new Map();
    /**
     * Texture cache
     *
     * This cache is used to avoid loading the same texture multiple times
     *
     * Once loaded, all textures are stored in the cache and deleted from the cache on their own when the texture is disposed
     *
     * key: requestString, value: texture
     */
    textureCache = new Map();
    _textureLoadInfoMap = new Map(); // key: requestString
    _loadingModels = new Map(); // key: uniqueId
    _errorTexturesReferenceCount = new Map(); // key: textureName, value: referenceModelCount
    static _EmptyResult = {
        texture: null,
        arrayBuffer: null
    };
    _incrementLeftLoadCount(uniqueId) {
        let model = this._loadingModels.get(uniqueId);
        if (model === undefined) {
            model = new TextureLoadingModel(uniqueId);
            this._loadingModels.set(uniqueId, model);
        }
        model.leftLoadCount += 1;
        let observable = this.onModelTextureLoadedObservable.get(uniqueId);
        if (observable === undefined) {
            observable = new Observable();
            this.onModelTextureLoadedObservable.set(uniqueId, observable);
        }
        return model;
    }
    _decrementLeftLoadCount(model) {
        model.leftLoadCount -= 1;
        if (!model.isRequesting && model.leftLoadCount === 0) {
            this._removeErrorTexturesReferenceCount(model.uniqueId);
            this._loadingModels.delete(model.uniqueId);
            const observable = this.onModelTextureLoadedObservable.get(model.uniqueId);
            observable?.notifyObservers();
            observable?.clear();
            this.onModelTextureLoadedObservable.delete(model.uniqueId);
        }
    }
    /**
     * Notify that the model texture load request has been ended
     *
     * If all the textures are cached, no event callback is called
     *
     * so this method must be called and specified at the end of the model's text load request to handle such an edge case
     *
     * @param uniqueId Model unique id
     */
    loadModelTexturesEnd(uniqueId) {
        const model = this._loadingModels.get(uniqueId);
        if (model === undefined)
            return;
        model.isRequesting = false;
        if (model.leftLoadCount === 0) {
            this._removeErrorTexturesReferenceCount(uniqueId);
            this._loadingModels.delete(uniqueId);
            const observable = this.onModelTextureLoadedObservable.get(uniqueId);
            observable?.notifyObservers();
            observable?.clear();
            this.onModelTextureLoadedObservable.delete(uniqueId);
        }
    }
    _addErrorTextureReferenceCount(uniqueId, textureData) {
        const model = this._loadingModels.get(uniqueId);
        model.errorTextureDatas.push(textureData);
        this._errorTexturesReferenceCount.set(textureData, (this._errorTexturesReferenceCount.get(textureData) ?? 0) + 1);
    }
    _removeErrorTexturesReferenceCount(uniqueId) {
        const model = this._loadingModels.get(uniqueId);
        for (let i = 0; i < model.errorTextureDatas.length; ++i) {
            const textureData = model.errorTextureDatas[i];
            const referenceCount = this._errorTexturesReferenceCount.get(textureData) - 1;
            if (referenceCount === 0) {
                if (textureData.texture !== null) {
                    textureData.texture.dispose();
                }
                else {
                    this._textureLoadInfoMap.delete(textureData.cacheKey);
                    this.textureCache.delete(textureData.cacheKey);
                    this._errorTexturesReferenceCount.delete(textureData);
                }
            }
            else {
                this._errorTexturesReferenceCount.set(textureData, referenceCount);
            }
        }
    }
    _handleTextureOnDispose(textureData) {
        textureData.texture.onDisposeObservable.addOnce(() => {
            this._textureLoadInfoMap.delete(textureData.cacheKey);
            this.textureCache.delete(textureData.cacheKey);
            this._errorTexturesReferenceCount.delete(textureData);
        });
    }
    async _loadTextureAsyncInternal(uniqueId, urlOrTextureName, arrayBufferOrBlob, sharedTextureIndex, scene, assetContainer) {
        const model = this._incrementLeftLoadCount(uniqueId);
        let textureLoadInfo = this._textureLoadInfoMap.get(urlOrTextureName);
        if (textureLoadInfo === undefined) {
            textureLoadInfo = new TextureLoadInfo();
            this._textureLoadInfoMap.set(urlOrTextureName, textureLoadInfo);
        }
        let textureData = this.textureCache.get(urlOrTextureName);
        if (textureData === undefined && !textureLoadInfo.hasLoadError) {
            const blobOrUrl = sharedTextureIndex !== null ? SharedToonTextures.Data[sharedTextureIndex]
                : urlOrTextureName;
            textureData = new MmdTextureData(urlOrTextureName, scene, assetContainer, blobOrUrl, arrayBufferOrBlob !== null, () => {
                this._handleTextureOnDispose(textureData);
                textureLoadInfo.hasLoadError = false;
                textureLoadInfo.observable.notifyObservers(false);
                textureLoadInfo.observable.clear();
            }, (_message, _exception) => {
                if (textureData.texture !== null)
                    this._handleTextureOnDispose(textureData);
                this._addErrorTextureReferenceCount(uniqueId, textureData);
                textureLoadInfo.hasLoadError = true;
                textureLoadInfo.observable.notifyObservers(true);
                textureLoadInfo.observable.clear();
            });
            this.textureCache.set(urlOrTextureName, textureData);
            const arrayBuffer = arrayBufferOrBlob instanceof Blob
                ? await arrayBufferOrBlob.arrayBuffer()
                : arrayBufferOrBlob;
            if (arrayBuffer !== null) {
                textureData.loadFromArrayBuffer(arrayBuffer);
            }
        }
        if (textureData.texture !== null && textureData.texture.isReady()) {
            this._decrementLeftLoadCount(model);
            if (textureLoadInfo.hasLoadError)
                return MmdAsyncTextureLoader._EmptyResult;
            return (textureData);
        }
        return new Promise((resolve) => {
            textureLoadInfo.observable.addOnce((hasLoadError) => {
                this._decrementLeftLoadCount(model);
                resolve(hasLoadError ? MmdAsyncTextureLoader._EmptyResult : textureData);
            });
        });
    }
    /**
     * Load texture asynchronously
     *
     * All texture requests for one model must be executed within one synchronization routine without await
     *
     * Because the internally used left texture count mechanism requires knowing the total number of textures required at the time the request is processed
     *
     * @param uniqueId Model unique id
     * @param rootUrl Root url
     * @param relativeTexturePathOrIndex Relative texture path or shared toon texture index
     * @param scene Scene
     * @param assetContainer Asset container
     * @returns MMD texture load result
     */
    async loadTextureAsync(uniqueId, rootUrl, relativeTexturePathOrIndex, scene, assetContainer) {
        const isSharedToonTexture = typeof relativeTexturePathOrIndex === "number";
        const finalRelativeTexturePath = isSharedToonTexture
            ? "file:shared_toon_texture_" + relativeTexturePathOrIndex
            : relativeTexturePathOrIndex;
        const requestString = isSharedToonTexture
            ? finalRelativeTexturePath
            : this.pathNormalize(rootUrl + relativeTexturePathOrIndex);
        return await this._loadTextureAsyncInternal(uniqueId, requestString, null, isSharedToonTexture ? relativeTexturePathOrIndex : null, scene, assetContainer);
    }
    /**
     * Load texture from buffer asynchronously
     *
     * All texture requests for one model must be executed within one synchronization routine without await
     *
     * Because the internally used left texture count mechanism requires knowing the total number of textures required at the time the request is processed
     *
     * @param uniqueId Model unique id
     * @param textureName Texture name
     * @param arrayBufferOrBlob Texture data encoded in PNG/JPG/BMP
     * @param scene Scene
     * @param assetContainer Asset container
     * @param applyPathNormalization Whether to apply path normalization to the texture name (default: true)
     * @returns MMD texture load result
     */
    async loadTextureFromBufferAsync(uniqueId, textureName, arrayBufferOrBlob, scene, assetContainer, applyPathNormalization = true) {
        if (applyPathNormalization) {
            textureName = this.pathNormalize(textureName);
        }
        return await this._loadTextureAsyncInternal(uniqueId, textureName, arrayBufferOrBlob, null, scene, assetContainer);
    }
    /**
     * Normalize path
     * @param path Path
     * @returns Normalized path
     */
    pathNormalize(path) {
        path = path.replace(/\\/g, "/");
        const pathArray = path.split("/");
        const resultArray = [];
        for (let i = 0; i < pathArray.length; ++i) {
            const pathElement = pathArray[i];
            if (pathElement === ".") {
                continue;
            }
            else if (pathElement === "..") {
                resultArray.pop();
            }
            else {
                resultArray.push(pathElement);
            }
        }
        return resultArray.join("/");
    }
}
